import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MapPin, Calendar, Clock, CheckCircle2, AlertCircle, XCircle, CalendarClock } from 'lucide-react';
import api from '../../services/api';

function formatData(iso) {
  if (!iso) return '';
  return new Date(String(iso).slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
}
function formatDiaCurto(iso) {
  if (!iso) return '';
  return new Date(String(iso).slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: '2-digit' });
}

export default function AgendamentoPublico() {
  const { token } = useParams();
  const [modo, setModo] = useState(null); // null | 'reagendar' | 'cancelar'
  const [dia, setDia] = useState('');
  const [horarioId, setHorarioId] = useState('');
  const [erro, setErro] = useState('');
  const [feito, setFeito] = useState(null); // { tipo: 'cancelado' | 'reagendado', dia, horario }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['agendamento-publico', token],
    queryFn: () => api.get(`/eventos/gerenciar/${token}`).then(r => r.data.data),
    retry: false,
  });

  const evento = data?.evento;
  const confirmacao = data?.confirmacao;
  const horarios = data?.horarios || [];
  const permiteReagendar = !!data?.permite_reagendar;
  const vagasEsgotadas = !!data?.vagas_esgotadas;
  const jaCancelado = confirmacao?.status === 'cancelado';
  const jaCompareceu = confirmacao?.status === 'compareceu';

  // Confirmação vinda de convite entra sem dia/horário próprios (não escolhe slot) — mostramos
  // a data do evento e, se houver um único horário cadastrado, ele.
  const horariosUnicos = [...new Set(horarios.map(h => h.horario).filter(Boolean))];
  const diaEfetivo = confirmacao?.dia || evento?.data_inicio || null;
  const horarioEfetivo = confirmacao?.horario || (horariosUnicos.length === 1 ? horariosUnicos[0] : '');

  const dias = useMemo(() => [...new Set(horarios.map(h => h.dia))].sort(), [horarios]);
  const horariosDoDia = useMemo(
    () => horarios.filter(h => h.dia === dia).sort((a, b) => String(a.horario).localeCompare(String(b.horario))),
    [horarios, dia],
  );

  const abrirReagendar = () => {
    setErro('');
    setModo('reagendar');
    const d = confirmacao?.dia ? String(confirmacao.dia).slice(0, 10) : (dias[0] || '');
    setDia(d);
    setHorarioId(confirmacao?.evento_horario_id || '');
  };

  const cancelarMut = useMutation({
    mutationFn: () => api.post(`/eventos/gerenciar/${token}/cancelar`),
    onSuccess: () => setFeito({ tipo: 'cancelado' }),
    onError: (e) => setErro(e?.response?.data?.error || 'Não foi possível cancelar. Tente de novo.'),
  });

  const reagendarMut = useMutation({
    mutationFn: () => api.post(`/eventos/gerenciar/${token}/reagendar`, { evento_horario_id: horarioId }),
    onSuccess: (res) => setFeito({ tipo: 'reagendado', ...res.data.data }),
    onError: (e) => setErro(e?.response?.data?.error || 'Não foi possível remarcar. Tente de novo.'),
  });

  const reabrirMut = useMutation({
    mutationFn: () => api.post(`/eventos/gerenciar/${token}/reabrir`),
    onSuccess: (res) => setFeito({ tipo: 'reaberto', ...res.data.data }),
    onError: (e) => {
      // evento com vários horários → cai pro seletor de horário (que já checa vaga por slot)
      if (e?.response?.data?.escolher_horario) { setErro(''); abrirReagendar(); return; }
      setErro(e?.response?.data?.error || 'Não foi possível confirmar sua presença de novo.');
    },
  });

  const salvarReagendamento = () => {
    setErro('');
    if (!horarioId) return setErro('Escolha um horário.');
    reagendarMut.mutate();
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-start justify-center p-4 py-10">
      <div className="w-full max-w-md">
        {isLoading && <div className="text-center text-gray-400 py-16">Carregando...</div>}

        {isError && (
          <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
            <AlertCircle className="mx-auto text-red-400 mb-3" size={32} />
            <p className="text-gray-700 font-medium">Não encontramos este agendamento.</p>
            <p className="text-sm text-gray-500 mt-1">O link pode ter expirado ou o evento foi removido.</p>
          </div>
        )}

        {evento && feito && (
          <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
            {feito.tipo === 'cancelado' ? (
              <>
                <XCircle className="mx-auto text-red-500 mb-3" size={40} />
                <h2 className="text-lg font-bold text-gray-800">Agendamento cancelado</h2>
                <p className="text-sm text-gray-500 mt-2">
                  Sua presença em «{evento.nome}» foi cancelada. Se mudar de ideia, é só usar o mesmo link
                  {permiteReagendar ? ' pra escolher um novo horário.' : ' ou falar com a nossa equipe.'}
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Enviamos a confirmação do cancelamento no seu WhatsApp e e-mail.
                </p>
                {permiteReagendar && (
                  <button
                    onClick={() => { setFeito(null); refetch(); abrirReagendar(); }}
                    className="btn-primary w-full py-3 mt-5"
                  >
                    Escolher um novo horário
                  </button>
                )}
              </>
            ) : (
              <>
                <CheckCircle2 className="mx-auto text-green-500 mb-3" size={40} />
                <h2 className="text-lg font-bold text-gray-800">
                  {feito.tipo === 'reaberto' ? 'Presença confirmada!' : 'Horário remarcado!'}
                </h2>
                <p className="text-sm text-gray-500 mt-2">
                  Sua presença em «{evento.nome}»
                  {feito.tipo === 'reaberto' ? ' está confirmada de novo' : ' agora é'}
                  {(feito.dia || diaEfetivo) ? ` — ${formatData(feito.dia || diaEfetivo)}` : ''}
                  {(feito.horario || horarioEfetivo) ? `, às ${feito.horario || horarioEfetivo}` : ''}.
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Enviamos a confirmação com o QR Code no seu WhatsApp e e-mail.
                </p>
              </>
            )}
          </div>
        )}

        {evento && !feito && (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {evento.imagem_url && (
              <img src={evento.imagem_url} alt={evento.nome} className="w-full max-h-72 object-cover" />
            )}
            <div className="bg-primary text-white px-6 py-5">
              <p className="text-xs uppercase tracking-wide text-white/70">Seu agendamento</p>
              <h1 className="text-lg font-bold">{evento.nome}</h1>
              {evento.local && <p className="text-sm text-white/80 flex items-center gap-1 mt-1"><MapPin size={13} /> {evento.local}</p>}
            </div>

            <div className="p-6 space-y-4">
              {/* Estado atual */}
              <div className={`rounded-xl border p-4 ${jaCancelado ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}>
                {jaCancelado ? (
                  <p className="text-sm text-red-700 font-medium">Este agendamento está cancelado.</p>
                ) : (
                  <>
                    <p className="text-sm text-gray-800 flex items-center gap-2">
                      <Calendar size={14} className="text-primary" />
                      {diaEfetivo ? formatData(diaEfetivo) : 'Data a definir'}
                    </p>
                    {horarioEfetivo && (
                      <p className="text-sm text-gray-800 flex items-center gap-2 mt-1">
                        <Clock size={14} className="text-primary" /> {horarioEfetivo}
                      </p>
                    )}
                  </>
                )}
              </div>

              {jaCompareceu && (
                <p className="text-sm text-gray-500">
                  Seu check-in já foi registrado — não é possível cancelar ou remarcar por aqui.
                </p>
              )}

              {/* Seletor de novo horário */}
              {modo === 'reagendar' && !jaCompareceu && (
                <div className="space-y-3">
                  {dias.length > 1 && (
                    <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Dia</label>
                      <div className="flex flex-wrap gap-2">
                        {dias.map(d => (
                          <button
                            key={d}
                            type="button"
                            onClick={() => { setDia(d); setHorarioId(''); }}
                            className={`px-3 py-2 rounded-lg text-sm border capitalize ${dia === d ? 'bg-primary text-white border-primary' : 'bg-white text-gray-700 border-gray-200'}`}
                          >
                            {formatDiaCurto(d)}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Horário</label>
                    <div className="grid grid-cols-3 gap-2">
                      {horariosDoDia.map(h => {
                        const atual = h.id === confirmacao?.evento_horario_id;
                        const lotado = h.vagas_restantes <= 0 && !atual;
                        return (
                          <button
                            key={h.id}
                            type="button"
                            disabled={lotado}
                            onClick={() => setHorarioId(h.id)}
                            className={`py-2 rounded-lg text-sm border ${horarioId === h.id ? 'bg-primary text-white border-primary' : lotado ? 'bg-gray-100 text-gray-300 border-gray-100 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-200'}`}
                          >
                            {h.horario}
                            {lotado && <span className="block text-[10px]">lotado</span>}
                          </button>
                        );
                      })}
                      {horariosDoDia.length === 0 && <p className="col-span-3 text-sm text-gray-400">Sem horários nesse dia.</p>}
                    </div>
                  </div>
                </div>
              )}

              {erro && <p className="text-sm text-red-600">{erro}</p>}

              {/* Ações */}
              {!jaCompareceu && (
                <div className="space-y-2 pt-1">
                  {modo === 'reagendar' ? (
                    <>
                      <button
                        onClick={salvarReagendamento}
                        disabled={reagendarMut.isPending}
                        className="btn-primary w-full py-3 disabled:opacity-50"
                      >
                        {reagendarMut.isPending ? 'Salvando...' : 'Salvar novo horário'}
                      </button>
                      <button onClick={() => { setModo(null); setErro(''); }} className="w-full py-2 text-sm text-gray-500">
                        Voltar
                      </button>
                    </>
                  ) : modo === 'cancelar' ? (
                    <>
                      <p className="text-sm text-gray-600 text-center">Tem certeza que quer cancelar sua presença?</p>
                      <button
                        onClick={() => cancelarMut.mutate()}
                        disabled={cancelarMut.isPending}
                        className="w-full py-3 rounded-lg bg-red-600 text-white font-medium disabled:opacity-50"
                      >
                        {cancelarMut.isPending ? 'Cancelando...' : 'Sim, cancelar minha presença'}
                      </button>
                      <button onClick={() => { setModo(null); setErro(''); }} className="w-full py-2 text-sm text-gray-500">
                        Não, manter
                      </button>
                    </>
                  ) : jaCancelado ? (
                    vagasEsgotadas ? (
                      <p className="text-sm text-gray-600 text-center bg-gray-50 border border-gray-200 rounded-lg p-4">
                        As vagas para este evento já estão preenchidas — não é possível confirmar sua presença de novo por aqui.
                      </p>
                    ) : permiteReagendar ? (
                      <button onClick={abrirReagendar} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                        <CalendarClock size={16} /> Escolher um horário
                      </button>
                    ) : (
                      <button
                        onClick={() => { setErro(''); reabrirMut.mutate(); }}
                        disabled={reabrirMut.isPending}
                        className="btn-primary w-full py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <CheckCircle2 size={16} /> {reabrirMut.isPending ? 'Confirmando...' : 'Confirmar presença de novo'}
                      </button>
                    )
                  ) : (
                    <>
                      {permiteReagendar && (
                        <button onClick={abrirReagendar} className="btn-primary w-full py-3 flex items-center justify-center gap-2">
                          <CalendarClock size={16} /> Alterar data / horário
                        </button>
                      )}
                      <button
                        onClick={() => { setModo('cancelar'); setErro(''); }}
                        className="w-full py-3 rounded-lg border border-red-200 text-red-600 font-medium"
                      >
                        Cancelar minha presença
                      </button>
                    </>
                  )}
                </div>
              )}

              {evento.local_mapa_url && (
                <a href={evento.local_mapa_url} target="_blank" rel="noreferrer" className="block text-center text-sm text-primary underline">
                  Ver local no mapa
                </a>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
